import * as React from 'react'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import { useGetTimeSlotGroupsQuery } from 'timeSlots/reduxQuery'
import { GroupDetail, useTimeSlotGroupDetails } from 'capacity/hooks/useTimeSlotGroupDetails'
import { GroupBlock } from './model/groupBlocks'

export interface ResolvedBlocks {
    blocks: GroupBlock[]
    // A sentence above the blocks, when the product gets different groups through different categories.
    lead?: string
}

export interface GroupBlocksResult {
    groups: TimeSlotGroup[] | undefined
    failed: boolean
    // null while the groups are still loading or the section is skipped.
    resolved: ResolvedBlocks | null
    details: Record<string, GroupDetail>
}

// Loads the account's time slot groups, resolves the ones that apply (resolve must be memoised), and fetches
// the slot times for them when the list endpoint leaves those out.
export function useGroupBlocks(
    accountSlug: string,
    skip: boolean,
    resolve: (groups: TimeSlotGroup[]) => ResolvedBlocks,
): GroupBlocksResult {
    const query = useGetTimeSlotGroupsQuery(accountSlug, { skip })
    const groups = query.data?.groups
    const resolved = React.useMemo(() => (groups && !skip ? resolve(groups) : null), [groups, skip, resolve])
    const neededIds = React.useMemo(
        () =>
            query.data && !query.data.slotsIncluded && resolved
                ? resolved.blocks.flatMap((block) => block.groups.map((group) => group.id))
                : [],
        [query.data, resolved],
    )
    const details = useTimeSlotGroupDetails(accountSlug, neededIds)
    return { groups, failed: query.isError, resolved, details }
}
